import { errorReporter } from "./errorReporter";
import { toError } from "./errorUtils";

/**
 * Utility function for API calls with error reporting (for React Query queries and mutations)
 */
export const withErrorReporting = async <T>(
  apiCall: () => Promise<T>,
  options: {
    componentName: string;
    operationName: string;
    endpoint?: string;
    method?: string;
    mutationType?: string;
    addFeatureBreadcrumb?: boolean;
    extra?: Record<string, unknown>;
  },
): Promise<T> => {
  const {
    componentName,
    operationName,
    endpoint = "/graphql",
    method = "POST",
    mutationType,
    addFeatureBreadcrumb = false,
    extra = {},
  } = options;

  try {
    errorReporter.addApiBreadcrumb(endpoint, method);
    if (addFeatureBreadcrumb) {
      errorReporter.addFeatureBreadcrumb(operationName, "started", extra);
    }

    const result = await apiCall();

    errorReporter.addApiBreadcrumb(endpoint, method, 200);
    if (addFeatureBreadcrumb) {
      errorReporter.addFeatureBreadcrumb(operationName, "completed", extra);
    }

    return result;
  } catch (error) {
    // Convert unknown error to proper Error object with GraphQL error handling
    const errorObj = toError(error, `${operationName} failed`);

    errorReporter.addApiBreadcrumb(endpoint, method, 0);
    if (addFeatureBreadcrumb) {
      errorReporter.addFeatureBreadcrumb(operationName, "failed", {
        ...extra,
        error: errorObj.message,
      });
    }

    errorReporter.reportApplicationError(errorObj, {
      tags: {
        component: componentName,
        operation: operationName,
        service: "graphql",
        ...(mutationType && { mutationType }),
      },
      extra: {
        endpoint,
        method,
        ...extra,
      },
    });

    throw errorObj;
  }
};
